import { httpTokenCommon } from "../helper/http-common";
import {
  QiCardCancelResponse,
  QiCardCreateOrderParams,
  QiCardCreateResponse,
  QiCardCreateWalletParams,
  QiCardStatusResponse,
} from "../types/qicard";

const QICARD_TYPE = "QiCard";

/** QiCard accepts "ar" or "en" only — not i18next locales like "en-US". */
export function normalizeQiCardLanguage(language?: string): "ar" | "en" {
  const raw = String(language ?? "").trim().toLowerCase();
  if (raw.startsWith("ar")) return "ar";
  return "en";
}

function normalizeCreateResponse(data: Record<string, unknown>): QiCardCreateResponse {
  return {
    transactionId: String(data.transactionId ?? ""),
    redirectUrl: String(data.redirectUrl ?? ""),
    externalReferenceId: data.externalReferenceId
      ? String(data.externalReferenceId)
      : undefined,
  };
}

function handleCreate409(err: unknown): QiCardCreateResponse | null {
  const axiosErr = err as {
    response?: { status?: number; data?: Record<string, unknown> };
  };
  if (axiosErr?.response?.status !== 409) return null;

  const d = axiosErr.response.data ?? {};
  return {
    transactionId: String(d.existingTransactionId ?? ""),
    redirectUrl: "",
    isDuplicate: true,
    existingStatus: d.existingStatus ? String(d.existingStatus) : undefined,
  };
}

class QiCardPaymentsService {
  async createOrderPayment(
    params: QiCardCreateOrderParams
  ): Promise<QiCardCreateResponse> {
    try {
      const response = await httpTokenCommon.post("/payments/create", {
        orderId: params.orderId,
        amount: params.amount,
        type: QICARD_TYPE,
        successUrl: params.successUrl,
        failedUrl: params.failedUrl,
        redirectUrl: params.redirectUrl,
        language: normalizeQiCardLanguage(params.language),
      });
      return normalizeCreateResponse(response.data as Record<string, unknown>);
    } catch (err) {
      const duplicate = handleCreate409(err);
      if (duplicate) return duplicate;
      throw err;
    }
  }

  async createWalletRecharge(
    params: QiCardCreateWalletParams
  ): Promise<QiCardCreateResponse> {
    const response = await httpTokenCommon.post("/payments/recharge", {
      amount: params.amount,
      type: QICARD_TYPE,
      successUrl: params.successUrl,
      failedUrl: params.failedUrl,
      redirectUrl: params.redirectUrl,
      language: normalizeQiCardLanguage(params.language),
    });
    return normalizeCreateResponse(response.data as Record<string, unknown>);
  }

  async getStatus(transactionId: string): Promise<QiCardStatusResponse> {
    const response = await httpTokenCommon.get(
      `/payments/status/${QICARD_TYPE}/${transactionId}`,
      { silent: true }
    );
    const data = response.data as Record<string, unknown>;
    return { status: String(data.status ?? data ?? "") };
  }

  async cancelPayment(transactionId: string): Promise<QiCardCancelResponse> {
    const response = await httpTokenCommon.post(
      `/payments/qicard/cancel/${transactionId}`,
      {},
      { silent: true }
    );
    const data = response.data as Record<string, unknown>;
    return {
      canceled: Boolean(data.canceled),
      status: data.status ? String(data.status) : undefined,
    };
  }
}

const qicardPayments = new QiCardPaymentsService();
export default qicardPayments;
